// Small pill/button used on member-facing event listings so an assigned
// Event Manager can jump directly to their event's management surface.
//
// Renders NOTHING when the current viewer cannot manage the event —
// safe to sprinkle into any card/row without additional guards.
//
// Never suppresses normal member actions (Register, Withdraw, Pay). The
// pill is additive.

import React from 'react';
import { Link } from 'react-router-dom';
import { Gear } from '@phosphor-icons/react';
import { useCanManageEvent, eventManageHref } from '../../hooks/useCanManageEvent';

/**
 * @param {object}  props
 * @param {'event_session'|'tournament'|'round_robin'|'club_event'} props.eventKind
 * @param {string}  props.eventId
 * @param {'row'|'card'} [props.variant]   Visual density hint.
 * @param {string}  [props.className]
 * @param {(e: React.MouseEvent)=>void} [props.onClick]  Optional extra hook (e.g. stop event bubbling).
 */
export default function ManageEventPill({ eventKind, eventId, variant = 'row', className = '', onClick }) {
  const can = useCanManageEvent(eventKind, eventId);
  if (!can) return null;
  const href = eventManageHref(eventKind, eventId);
  if (!href) return null;

  const base = variant === 'card'
    ? 'text-[11px] px-2.5 py-1'
    : 'text-[10px] px-2 py-0.5';
  return (
    <Link
      to={href}
      onClick={onClick}
      data-testid={`manage-pill-${eventKind}-${eventId}`}
      className={`inline-flex items-center gap-1 rounded-full bg-[#013523] text-white ${base} font-medium hover:bg-[#024029] ${className}`}
    >
      <Gear size={10} weight="bold" /> Manage
    </Link>
  );
}
